import { WIDGET_CAPABILITIES } from "./capabilities";
import type { DashboardWidget, WidgetLayout, WidgetType } from "./types";

/**
 * A grade do painel em CÉLULAS.
 *
 * Todo widget ocupa um número inteiro de células, e o operador estica ou
 * encolhe arrastando o canto. É o modelo que o dono pediu depois de três
 * tentativas minhas de decidir o tamanho por ele:
 *
 *  - `sm`/`md`/`lg` fixava três larguras e nenhuma altura;
 *  - o span por TIPO acertava gráfico e errava todo o resto;
 *  - a altura por SEMÂNTICA encolhia o card e não a fileira, porque o CSS
 *    grid iguala a fileira pela maior — e eu disse que empacotar de verdade
 *    exigiria altura previsível, que não existia.
 *
 * A célula é exatamente essa altura previsível. Com `grid-auto-rows` de uma
 * célula e `grid-row: span N`, os cards se encaixam por construção, e o buraco
 * embaixo de um card baixo desaparece sem precisar agrupar nada.
 */

/** Lado da célula, em px. Uma constante — mudar aqui muda a malha inteira. */
export const CELL_SIZE = 68;
/** Respiro entre células. `CELL_SIZE + CELL_GAP` é o passo da malha (80 px). */
export const CELL_GAP = 12;

/** Teto de segurança: um widget não pode ficar maior que a tela útil. */
export const MAX_CELLS_W = 16;
export const MAX_CELLS_H = 12;

export interface CellSize {
  w: number;
  h: number;
}

/**
 * Menor tamanho em que o card ainda se lê. O arrasto trava aqui.
 *
 * Existe porque redimensionar livre deixa o operador produzir um card
 * ilegível — uma curva de 24 h em duas células vira um risco, e um comparativo
 * de três fases em duas colunas empilha os números. Os valores moram na tabela
 * de capacidades, junto das outras perguntas que todo tipo novo responde.
 */
export function minCells(widgetType: WidgetType): CellSize {
  return WIDGET_CAPABILITIES[widgetType].cells.min;
}


/**
 * Tamanho de NASCIMENTO por tipo. É só o ponto de partida — a partir daí quem
 * manda é o arrasto.
 */
export function defaultCells(widgetType: WidgetType): CellSize {
  return WIDGET_CAPABILITIES[widgetType].cells.default;
}

export function clampCells(size: CellSize, widgetType: WidgetType): CellSize {
  const min = minCells(widgetType);
  return {
    w: Math.max(min.w, Math.min(MAX_CELLS_W, Math.round(size.w))),
    h: Math.max(min.h, Math.min(MAX_CELLS_H, Math.round(size.h))),
  };
}

/**
 * Larguras do enum antigo, em células. Serve só para a migração: um layout
 * salvo com `sm`/`md`/`lg` não pode perder a escolha que o operador tinha
 * feito só porque a malha mudou.
 */
const LEGACY_WIDTH: Record<WidgetLayout["size"], number> = { sm: 4, md: 7, lg: 10 };

/**
 * Tamanho efetivo em células.
 *
 * Ordem: o que o operador arrastou > o que ele escolheu no enum antigo > o
 * default do tipo. A altura do enum antigo não existia, então vem do default —
 * é a única parte do layout legado que não dá para preservar, porque nunca foi
 * gravada.
 */
export function widgetCells(widget: DashboardWidget): CellSize {
  const { layout, widgetType } = widget;
  if (typeof layout.w === "number" && typeof layout.h === "number") {
    return clampCells({ w: layout.w, h: layout.h }, widgetType);
  }
  // Sem celulas = nunca houve escolha de tamanho. O enum antigo NAO e mais
  // consultado aqui: `normalizeLayout` o converte para `w`/`h` na leitura, uma
  // vez, entao quem chegou ate este ponto realmente nao escolheu nada.
  return clampCells(defaultCells(widgetType), widgetType);
}

/**
 * Converte o tamanho do enum ANTIGO em celulas. Usada so pela migracao de
 * leitura em `normalizeLayout`.
 *
 * A altura vem do default do tipo porque o enum nunca teve altura — e a unica
 * parte do layout legado que nao da para preservar, por nunca ter existido.
 */
export function cellsFromLegacySize(
  size: DashboardWidget["layout"]["size"],
  widgetType: WidgetType,
): CellSize {
  const fallback = defaultCells(widgetType);
  return clampCells({ w: LEGACY_WIDTH[size] ?? fallback.w, h: fallback.h }, widgetType);
}

/** Converte um delta em px arrastado no canto para um delta em células. */
export function cellsFromPixels(deltaX: number, deltaY: number): CellSize {
  const step = CELL_SIZE + CELL_GAP;
  return { w: Math.round(deltaX / step), h: Math.round(deltaY / step) };
}

/* ─────────────── POSICAO NA MALHA ─────────────── */

/** Retangulo de celulas ocupado por um card. `x`/`y` sao base zero. */
export interface CellRect extends CellSize {
  x: number;
  y: number;
}

/** Dois cards disputam alguma celula? */
export function overlaps(a: CellRect, b: CellRect): boolean {
  return (
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
  );
}

/**
 * O destino cabe? Precisa estar dentro das colunas E livre.
 *
 * A regra e RECUSAR, nao empurrar. Empurrar vizinho em cascata (estilo
 * Grafana) produz layout que o operador nao pediu — e exige compactar na
 * vertical para os empurroes nao se acumularem, o que por sua vez faz card
 * flutuar para cima e mata o buraco deliberado. Recusar mantem a promessa: o
 * card fica onde foi solto, ou nao vai.
 */
export function fits(
  alvo: CellRect,
  ocupadas: readonly CellRect[],
  columns: number,
): boolean {
  if (alvo.x < 0 || alvo.y < 0) return false;
  if (alvo.x + alvo.w > columns) return false;
  return !ocupadas.some((outro) => overlaps(alvo, outro));
}

/**
 * Encosta o destino nos limites horizontais, para arrastar para fora da
 * borda nao virar recusa — o operador quis a coluna extrema, nao o vazio.
 * A vertical nao tem teto: o painel cresce para baixo.
 */
export function clampToColumns(alvo: CellRect, columns: number): CellRect {
  return {
    ...alvo,
    x: Math.max(0, Math.min(Math.max(0, columns - alvo.w), alvo.x)),
    y: Math.max(0, alvo.y),
  };
}

/** Quantas colunas cabem numa largura util. Espelha o `auto-fill` do CSS. */
export function columnsForWidth(widthPx: number): number {
  return Math.max(1, Math.floor((widthPx + CELL_GAP) / (CELL_SIZE + CELL_GAP)));
}

/** Posicao efetiva do card, quando ja foi colocada. */
export function widgetCell(widget: DashboardWidget): { x: number; y: number } | null {
  const { x, y } = widget.layout;
  return typeof x === "number" && typeof y === "number" ? { x, y } : null;
}
